import React, { useState, useEffect } from 'react';
import { Form, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { FaVolumeUp, FaVolumeMute, FaPlay, FaStop } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  isHrLoginSoundEnabled,
  updateHrLoginSoundSetting,
  subscribeHrLoginSoundSetting,
  playHrLoginSound,
  stopHrLoginSound
} from '../../utils/hrAudioService';

const HRSoundToggle = ({ user }) => {
  const [enabled, setEnabled] = useState(isHrLoginSoundEnabled());
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    // Listen to real-time changes
    const unsubscribe = subscribeHrLoginSoundSetting((latestVal) => {
      setEnabled(latestVal);
    });

    return () => {
      unsubscribe();
      stopHrLoginSound();
    };
  }, []);

  const handleToggle = async (e) => {
    const nextVal = e.target.checked;
    setEnabled(nextVal);
    setSaving(true);

    try {
      const email = user?.email || user?.personal?.email || 'admin';
      const success = await updateHrLoginSoundSetting(nextVal, email);
      if (success) {
        if (nextVal) {
          toast.success('🔊 HR Login Sound enabled!');
        } else {
          toast.info('🔇 HR Login Sound disabled!');
          stopHrLoginSound();
          setPreviewing(false);
        }
      } else {
        toast.error('Failed to update sound setting.');
        setEnabled(!nextVal);
      }
    } catch (err) {
      console.error('Error toggling HR sound:', err);
      toast.error('Failed to update sound setting.');
      setEnabled(!nextVal);
    } finally {
      setSaving(false);
    }
  };

  const handleTestSound = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (previewing) {
      stopHrLoginSound();
      setPreviewing(false);
    } else {
      setPreviewing(true);
      playHrLoginSound(4);
      setTimeout(() => {
        setPreviewing(false);
      }, 4000);
    }
  };

  return (
    <div
      className="d-flex align-items-center gap-2 px-2 py-1 rounded-pill"
      style={{
        background: enabled ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
        border: enabled ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(100, 116, 139, 0.25)',
        transition: 'all 0.25s ease'
      }}
    >
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="hr-sound-preview-tooltip">
            {previewing ? 'Stop test sound' : 'Test sugarcane juice sound (4s)'}
          </Tooltip>
        }
      >
        <button
          type="button"
          onClick={handleTestSound}
          className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: previewing ? '#ef4444' : (enabled ? '#10b981' : '#64748b'),
            color: '#ffffff',
            fontSize: '10px',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
          }}
          title={previewing ? 'Stop' : 'Play Preview'}
        >
          {previewing ? <FaStop /> : (enabled ? <FaPlay style={{ marginLeft: '1px' }} /> : <FaVolumeMute />)}
        </button>
      </OverlayTrigger>

      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="hr-sound-toggle-tooltip">
            {enabled
              ? 'HR Login Sound is ON (plays sugarcane sound for 4s when HR logs in)'
              : 'HR Login Sound is OFF (muted on HR login)'}
          </Tooltip>
        }
      >
        <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
          <span
            className="fw-bold"
            style={{
              fontSize: '0.75rem',
              color: enabled ? '#059669' : '#64748b',
              letterSpacing: '0.2px',
              userSelect: 'none'
            }}
          >
            HR Sound:
          </span>
          <span
            className="badge rounded-pill fw-bold"
            style={{
              fontSize: '0.65rem',
              background: enabled ? '#10b981' : '#94a3b8',
              color: '#ffffff',
              padding: '2px 6px',
              userSelect: 'none'
            }}
          >
            {enabled ? 'ON' : 'OFF'}
          </span>
        </div>
      </OverlayTrigger>

      <Form.Check
        type="switch"
        id="hr-login-sound-switch"
        checked={enabled}
        disabled={saving}
        onChange={handleToggle}
        className="m-0 custom-sound-switch"
        style={{
          cursor: saving ? 'wait' : 'pointer',
          transform: 'scale(0.85)'
        }}
      />

      {saving && <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />}
    </div>
  );
};

export default HRSoundToggle;
